/* global __ENV_VARS__ */
export interface EnvironmentConfig {
  name: string
  apiBaseUrl: string
  authApiUrl: string
  wsBaseUrl: string
  httpTimeout: number
  enableDebug: boolean
  enableAnalytics: boolean
  enableErrorReporting: boolean
  logLevel: 'debug' | 'info' | 'warn' | 'error'
  features: {
    experimentalFeatures: boolean
    betaFeatures: boolean
    advancedTools: boolean
  }
  performance: {
    enableCaching: boolean
    enableCompression: boolean
    enableMinification: boolean
  }
  security: {
    enableCSP: boolean
    enableHSTS: boolean
    enableXSSProtection: boolean
  }
  websocket: {
    reconnectAttempts: number
    reconnectBaseDelay: number
    reconnectMaxDelay: number
  }
  /** Optional dev-only wpcauth token (e.g. VITE_WPCAUTH_DEV_TOKEN) for yb360/core1 when no real login; empty in prod. */
  wpcauthDevToken: string
  /** Local-dev-only: log the whole decoded SpreadCheckpoint to console once per spread load (VITE_LOG_PROTOBUF_CHECKPOINT). */
  enableProtobufCheckpointLogging: boolean
  /** Local-dev-only: log the spread's reconstructed NSD XML once per spread load (VITE_LOG_SPREAD_XML). */
  enableSpreadXmlLogging: boolean
  /**
   * Optional dev-only landing page override (e.g. VITE_DEV_LANDING_PAGE_NUMBER=47 opens the spread
   * containing yearbook page 47 after login). Matches `SpreadPage.number`, not the spread index/number.
   * 0 (default) means unset. Only ever consulted when `import.meta.env.DEV` is true — see `home.tsx`.
   */
  devLandingPageNumber: number
  /**
   * Origins allowed to embed Larry in an iframe and hand it a session (`/embed`), e.g. Mary's
   * domain. Comma-separated in VITE_EMBED_PARENT_ORIGINS; empty means embedding is refused.
   */
  embedParentOrigins: string[]
}

// Helper function to get environment variable with fallback
const getEnvVar = (key: string, fallback: string): string => {
  return __ENV_VARS__?.[key] || fallback
}

// Helper function to get a comma-separated list environment variable (trimmed, blanks dropped)
const getEnvList = (key: string): string[] =>
  (__ENV_VARS__?.[key] ?? '')
    .split(',')
    .map((item: string) => item.trim())
    .filter(Boolean)

// Helper function to get boolean environment variable
const getEnvBool = (key: string, fallback: boolean): boolean => {
  const value = __ENV_VARS__?.[key]

  if (value === undefined) {
    return fallback
  }

  return value === 'true'
}

// Helper function to get number environment variable
const getEnvNumber = (key: string, fallback: number): number => {
  const value = __ENV_VARS__?.[key]

  if (value === undefined) {
    return fallback
  }

  const parsed = Number.parseInt(value, 10)

  if (Number.isNaN(parsed)) {
    return fallback
  }

  return parsed
}

// Helper function to get environment name
const getEnvironmentName = (): string => {
  const env = process.env.NODE_ENV || 'dev'

  switch (env) {
    case 'dev':
    case 'development':
      return 'Local'
    case 'staging':
      return 'Staging'
    case 'production':
      return 'Production'
    case 'test':
      return 'Test'
    default:
      return 'Local'
  }
}

export const getEnvironmentConfig = (): EnvironmentConfig => {
  const environmentName = getEnvironmentName()

  return {
    name: environmentName,
    apiBaseUrl: getEnvVar('VITE_API_BASE_URL', 'http://localhost:3001/api'),
    authApiUrl: getEnvVar('VITE_AUTH_API_URL', 'https://yb360.dev-wpcmodx.com'),
    wsBaseUrl: getEnvVar('VITE_WS_BASE_URL', 'ws://localhost:3002'),
    httpTimeout: getEnvNumber('VITE_HTTP_TIMEOUT', 30000), // 30 seconds default
    enableDebug: getEnvBool('VITE_ENABLE_DEBUG', true),
    enableAnalytics: getEnvBool('VITE_ENABLE_ANALYTICS', false),
    enableErrorReporting: getEnvBool('VITE_ENABLE_ERROR_REPORTING', false),
    logLevel: getEnvVar('VITE_LOG_LEVEL', 'debug') as 'debug' | 'info' | 'warn' | 'error',
    features: {
      experimentalFeatures: getEnvBool('VITE_ENABLE_EXPERIMENTAL_FEATURES', true),
      betaFeatures: getEnvBool('VITE_ENABLE_BETA_FEATURES', true),
      advancedTools: getEnvBool('VITE_ENABLE_ADVANCED_TOOLS', true),
    },
    performance: {
      enableCaching: getEnvBool('VITE_ENABLE_CACHING', false),
      enableCompression: getEnvBool('VITE_ENABLE_COMPRESSION', false),
      enableMinification: getEnvBool('VITE_ENABLE_MINIFICATION', false),
    },
    security: {
      enableCSP: getEnvBool('VITE_ENABLE_CSP', false),
      enableHSTS: getEnvBool('VITE_ENABLE_HSTS', false),
      enableXSSProtection: getEnvBool('VITE_ENABLE_XSS_PROTECTION', false),
    },
    websocket: {
      reconnectAttempts: getEnvNumber('VITE_WS_RECONNECT_ATTEMPTS', 10),
      reconnectBaseDelay: getEnvNumber('VITE_WS_RECONNECT_BASE_DELAY', 1000),
      reconnectMaxDelay: getEnvNumber('VITE_WS_RECONNECT_MAX_DELAY', 30000),
    },
    wpcauthDevToken: getEnvVar('VITE_WPCAUTH_DEV_TOKEN', ''),
    enableProtobufCheckpointLogging: getEnvBool('VITE_LOG_PROTOBUF_CHECKPOINT', false),
    enableSpreadXmlLogging: getEnvBool('VITE_LOG_SPREAD_XML', false),
    devLandingPageNumber: getEnvNumber('VITE_DEV_LANDING_PAGE_NUMBER', 0),
    embedParentOrigins: getEnvList('VITE_EMBED_PARENT_ORIGINS'),
  }
}

export const isLocal = (): boolean => {
  return process.env.NODE_ENV === 'dev' || process.env.NODE_ENV === 'development'
}

export const isStaging = (): boolean => {
  return process.env.NODE_ENV === 'staging'
}

export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production'
}

export const isTest = (): boolean => {
  return process.env.NODE_ENV === 'test'
}
